import { measurer } from "neql"
import { FilterAndSortExpression } from "./FilterAndSortExpression"
import {
    BinaryFilterOperator,
    FilterExpression,
    UnaryFilterOperator
} from "./FilterExpression"

export interface SuperAILabelerModel {
    uuid: string
    email: string
    isActive: boolean
    taskCompleted: number
    taskMedianTime: number
    reviewedCount?: number
    reviewScore?: number
    lastLabeled: string
    trustScore?: number
}

export type SuperAILabelerParams = Omit<
    measurer.LabelerStatsListByAppIdQueryVariables,
    "limit" | "offset" | "appId"
>

export function decodeSuperAILabelerParams({
    sort,
    filter = []
}: FilterAndSortExpression<SuperAILabelerModel>): SuperAILabelerParams {
    function decodeSort(): SuperAILabelerParams {
        const order = sort.desc ? measurer.Order_By.Desc : measurer.Order_By.Asc

        switch (sort.sortBy) {
            case "reviewScore":
                return { ordering: { review_score: order } }
            case "taskCompleted":
                return { ordering: { n_tasks: order } }
            case "taskMedianTime":
                return { ordering: { median_duration_sec: order } }
            case "trustScore":
                return { ordering: { trust_score: order } }
            case "lastLabeled":
                return { ordering: { worker: { updated_at: order } } }
            case "email":
                return { ordering: { worker: { email: order } } }
            case "isActive":
                return { ordering: { app: { active: order } } }
            default:
                throw Error(`Unsupported sort: ${sort.sortBy}`)
        }
    }
    function decodeFilter(
        accum: SuperAILabelerParams,
        f: FilterExpression<SuperAILabelerModel>
    ): SuperAILabelerParams {
        switch (f.fieldName) {
            case "email":
                switch (f.operator) {
                    case UnaryFilterOperator.contains:
                        return { ...accum, emailLike: `%${f.value}%` }
                    default:
                        return accum
                }
            case "taskCompleted":
                switch (f.operator) {
                    case UnaryFilterOperator.ge:
                        return { ...accum, taskCompletedLargerThan: f.value }
                    case UnaryFilterOperator.le:
                        return { ...accum, taskCompletedSmallerThan: f.value }
                    case BinaryFilterOperator.between:
                        return {
                            ...accum,
                            taskCompletedSmallerThan: f.value[1],
                            taskCompletedLargerThan: f.value[0]
                        }
                    default:
                        return accum
                }
            case "taskMedianTime":
                switch (f.operator) {
                    case UnaryFilterOperator.ge:
                        return { ...accum, taskTimeLargerThan: f.value }
                    case UnaryFilterOperator.le:
                        return { ...accum, taskTimeSmallerThan: f.value }
                    case BinaryFilterOperator.between:
                        return {
                            ...accum,
                            taskTimeSmallerThan: f.value[1],
                            taskTimeLargerThan: f.value[0]
                        }
                    default:
                        return accum
                }
            case "reviewScore":
                switch (f.operator) {
                    case UnaryFilterOperator.ge:
                        return {
                            ...accum,
                            reviewScoreLargerThan: f.value
                        }
                    case UnaryFilterOperator.le:
                        return {
                            ...accum,
                            reviewScoreSmallerThan: f.value
                        }
                    case BinaryFilterOperator.between:
                        return {
                            ...accum,
                            reviewScoreSmallerThan: f.value[1],
                            reviewScoreLargerThan: f.value[0]
                        }
                    default:
                        return accum
                }
            case "trustScore":
                switch (f.operator) {
                    case UnaryFilterOperator.ge:
                        return {
                            ...accum,
                            trustScoreLargerThan: f.value
                        }
                    case UnaryFilterOperator.le:
                        return {
                            ...accum,
                            trustScoreSmallerThan: f.value
                        }
                    case BinaryFilterOperator.between:
                        return {
                            ...accum,
                            trustScoreSmallerThan: f.value[1],
                            trustScoreLargerThan: f.value[0]
                        }
                    default:
                        return accum
                }
            case "lastLabeled":
                switch (f.operator) {
                    case UnaryFilterOperator.ge:
                        return {
                            ...accum,
                            updatedAtLargerThan: f.value
                        }
                    case UnaryFilterOperator.le:
                        return {
                            ...accum,
                            updatedAtSmallerThan: f.value
                        }
                    case BinaryFilterOperator.between:
                        return {
                            ...accum,
                            updatedAtSmallerThan: f.value[1],
                            updatedAtLargerThan: f.value[0]
                        }
                    default:
                        return accum
                }
            case "isActive":
                switch (f.operator) {
                    case UnaryFilterOperator.eq:
                        return { ...accum, active: f.value }
                    default:
                        return accum
                }
            default:
                return accum
        }
    }

    return filter.reduce(decodeFilter, decodeSort())
}

export function getSuperAILabelerID(model: SuperAILabelerModel): string {
    return model.uuid
}
